import {
  ApplyType,
  ContactPerson,
  Currency,
  DirectJobForm,
  JobType,
  SalaryPeriod,
  WorkplaceType,
} from "../../redux/reducer/directJobs.types";

export type JobFormValues = {
  title: string;
  department: string;
  category: string;
  location: string;
  workplace_type: WorkplaceType;
  job_type: JobType;
  has_no_deadline: boolean;
  application_deadline: string;
  salary: {
    currency: Currency | "";
    from: string;
    to: string;
    period: SalaryPeriod | "";
  };
  ad_text: string;
  banner_url: string;
  contact_persons: ContactPerson[];
  apply: { type: ApplyType; url: string; email: string };
  job_ad_template_id: string;
  show_from: string;
  show_until: string;
};

export const EMPTY_JOB_FORM_VALUES: JobFormValues = {
  title: "",
  department: "",
  category: "",
  location: "",
  workplace_type: WorkplaceType.NOT_SPECIFIED,
  job_type: JobType.NOT_SPECIFIED,
  has_no_deadline: true,
  application_deadline: "",
  salary: { currency: "", from: "", to: "", period: "" },
  ad_text: "",
  banner_url: "",
  contact_persons: [],
  apply: { type: ApplyType.URL, url: "", email: "" },
  job_ad_template_id: "",
  show_from: "",
  show_until: "",
};

export const EMPTY_CONTACT_PERSON: ContactPerson = {
  name: "",
  title: "",
  email: "",
  phone: "",
};

const pad = (value: number) => String(Math.abs(value)).padStart(2, "0");

const localDateTime = (date: string, time: string) => {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes, seconds] = time.split(":").map(Number);
  const offset = -new Date(
    year,
    month - 1,
    day,
    hours,
    minutes,
    seconds
  ).getTimezoneOffset();
  const sign = offset < 0 ? "-" : "+";
  return `${date}T${time}${sign}${pad(Math.trunc(offset / 60))}:${pad(
    offset % 60
  )}`;
};

export const toLocalDateString = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const storedDate = (dateTime: string) => dateTime.slice(0, 10);

const orNull = (value: string) => value || null;

export const parseAmount = (value: string) => Number(value.replace(",", "."));

const toAmount = (value: string) => (value === "" ? null : parseAmount(value));

export const toDirectJobForm = (values: JobFormValues): DirectJobForm => {
  const { currency, from, to, period } = values.salary;
  const hasSalary = Boolean(currency || from || to || period);
  return {
    title: values.title,
    department: orNull(values.department),
    category: orNull(values.category),
    location: orNull(values.location),
    workplace_type: values.workplace_type,
    job_type: values.job_type,
    application_deadline: values.has_no_deadline
      ? null
      : orNull(values.application_deadline),
    salary: hasSalary
      ? {
          currency: currency || null,
          from: toAmount(from),
          to: toAmount(to),
          period: period || null,
        }
      : null,
    ad_text: orNull(values.ad_text),
    banner_url: orNull(values.banner_url),
    contact_persons: values.contact_persons,
    apply: {
      type: values.apply.type,
      url: values.apply.type === ApplyType.URL ? values.apply.url : "",
      email: values.apply.type === ApplyType.EMAIL ? values.apply.email : "",
    },
    job_ad_template_id: orNull(values.job_ad_template_id),
    show_from: values.show_from
      ? localDateTime(values.show_from, "00:00:00")
      : null,
    show_until: values.show_until
      ? localDateTime(values.show_until, "23:59:59")
      : null,
  };
};

export const toJobFormValues = (form: DirectJobForm): JobFormValues => ({
  title: form.title,
  department: form.department ?? "",
  category: form.category ?? "",
  location: form.location ?? "",
  workplace_type: form.workplace_type,
  job_type: form.job_type,
  has_no_deadline: form.application_deadline === null,
  application_deadline: form.application_deadline ?? "",
  salary: form.salary
    ? {
        currency: form.salary.currency ?? "",
        from: form.salary.from === null ? "" : String(form.salary.from),
        to: form.salary.to === null ? "" : String(form.salary.to),
        period: form.salary.period ?? "",
      }
    : EMPTY_JOB_FORM_VALUES.salary,
  ad_text: form.ad_text ?? "",
  banner_url: form.banner_url ?? "",
  contact_persons: form.contact_persons,
  apply: form.apply ?? EMPTY_JOB_FORM_VALUES.apply,
  job_ad_template_id: form.job_ad_template_id ?? "",
  show_from: form.show_from ? storedDate(form.show_from) : "",
  show_until: form.show_until ? storedDate(form.show_until) : "",
});
